import { cn } from "@/lib/utils";
import { Reveal } from "@/components/Reveal";
import type { HTMLAttributes, ReactNode } from "react";

/**
 * Single source of truth for top-level page width, horizontal padding,
 * and vertical rhythm. Every route in the review cycle (and any other
 * standard page) MUST wrap its main content in <PageContainer> instead
 * of repeating `mx-auto max-w-6xl px-6 py-12` inline — that's how the
 * layout stays uniform and stops "jumping around" between pages.
 *
 * By default the contents fade/slide in on mount using the shared
 * Reveal animation, matching the scroll-reveal behaviour on the
 * landing page. Pass `animate={false}` to opt out.
 */
export interface PageContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Optional override for the wrapper element (defaults to `div`). */
  as?: "div" | "main" | "article" | "section";
  /** Animate contents on mount (default true). */
  animate?: boolean;
}

export function PageContainer({
  children,
  className,
  as: Tag = "div",
  animate = true,
  ...rest
}: PageContainerProps) {
  return (
    <Tag
      className={cn("mx-auto max-w-6xl px-6 py-12", className)}
      {...rest}
    >
      {animate ? <Reveal y={16}>{children}</Reveal> : children}
    </Tag>
  );
}



